import React from "react";
import { Users, FileText } from "lucide-react";
import { useLanguage } from "@/context/LanguageContext";
import { LEAD_STATUSES } from "@/components/admin/StatusBadge";

const StatCard = ({ icon, value, label, iconClass, testId }) => (
  <div className="flex items-center gap-4 rounded-2xl border border-[color:var(--border-subtle)] bg-white p-5 shadow-[var(--shadow-sm)]" data-testid={testId}>
    <span className={`flex h-12 w-12 items-center justify-center rounded-xl ${iconClass}`}>{icon}</span>
    <div>
      <div className="text-2xl font-bold text-[var(--brand-navy)]">{value}</div>
      <div className="text-sm text-[var(--muted-ink)]">{label}</div>
    </div>
  </div>
);

const DOT = {
  new: "bg-[var(--brand-burgundy)]",
  contacted: "bg-[var(--brand-navy)]",
  in_progress: "bg-[var(--warning)]",
  closed: "bg-[var(--success)]",
};

/** Clickable per-status counters that double as quick filters. */
const StatusBreakdown = ({ byStatus, activeStatus, onStatusClick }) => {
  const { t } = useLanguage();
  return (
    <div className="flex flex-wrap gap-2" data-testid="status-breakdown">
      {LEAD_STATUSES.map((s) => {
        const active = activeStatus === s;
        return (
          <button
            key={s}
            type="button"
            onClick={() => onStatusClick(active ? "" : s)}
            aria-pressed={active}
            className={`inline-flex items-center gap-2 rounded-full border px-3 py-1.5 text-xs font-semibold transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--focus-ring)] ${
              active
                ? "border-[var(--brand-burgundy)] bg-[rgba(139,30,63,0.08)] text-[var(--brand-burgundy)]"
                : "border-[color:var(--border-subtle)] bg-white text-[var(--brand-charcoal)] hover:bg-[var(--surface-2)]"
            }`}
            data-testid={`status-count-${s}`}
          >
            <span className={`h-2 w-2 rounded-full ${DOT[s]}`} aria-hidden="true" />
            {t.admin.statuses[s]}
            <span className="rounded-full bg-[var(--surface-2)] px-1.5 py-0.5 text-[11px] font-bold text-[var(--brand-navy)]" data-testid={`status-count-value-${s}`}>
              {byStatus?.[s] ?? 0}
            </span>
          </button>
        );
      })}
    </div>
  );
};

export const StatsPanel = ({ stats, activeStatus = "", onStatusClick = () => {} }) => {
  const { t } = useLanguage();
  return (
    <div className="space-y-4">
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
        <StatCard
          testId="stat-leads"
          icon={<Users className="h-6 w-6" />}
          iconClass="bg-[rgba(139,30,63,0.08)] text-[var(--brand-burgundy)]"
          value={stats.leads}
          label={t.admin.totalLeads}
        />
        <StatCard
          testId="stat-docs"
          icon={<FileText className="h-6 w-6" />}
          iconClass="bg-[var(--surface-2)] text-[var(--brand-charcoal)]"
          value={stats.documents}
          label={t.admin.totalDocs}
        />
      </div>
      <StatusBreakdown byStatus={stats.by_status} activeStatus={activeStatus} onStatusClick={onStatusClick} />
    </div>
  );
};
